<template>
  <div>
    <b-field :class="$style.modeSection">
      <p class="control">
        <b-button
          @click="switchMode('aggregate')"
          :class="[
            $style.button,
            dataCollectionType === 'aggregate' && $style.buttonActive,
          ]"
        >
          <img
            :src="
              require(dataCollectionType === 'aggregate'
                ? '@/assets/icons/ic_aggregated_white.png'
                : '@/assets/icons/ic_aggregated_grey.png')
            "
          />
          Aggregate
        </b-button>
      </p>
      <p class="control">
        <b-button
          @click="switchMode('individual')"
          :class="[
            $style.button,
            dataCollectionType === 'individual' && $style.buttonActive,
          ]"
        >
          <img
            :src="
              require(dataCollectionType === 'individual'
                ? '@/assets/icons/ic_individual_white.png'
                : '@/assets/icons/ic_individual_grey.png')
            "
          />
          Individual
        </b-button>
      </p>
    </b-field>
    <div :class="$style.dataContainer" v-if="renderComponent">
      <template v-if="dataCollectionType === 'aggregate' && renderComponent">
        <DataCollectionAggregate
          v-if="defaultSiteId !== undefined"
          :auditDetail="auditDetail"
          :singleAuditCriteriaId="undefined"
          :editDataCollectionRecord="null"
          :dataCollectionSummary="dirtyDataCollectionSummary"
          :defaultSiteId="defaultSiteId"
          :siteList="siteList"
          :auditCriteriaList="auditCriteriaList"
          :auditConfig="auditConfig"
          @submitRecords="handleEnteredDataRecords"
          @updateSiteDropdownItem="handleSiteDropdownItem"
          @isMultipleSubmission="
            (isSubmitAnotherRecord) =>
              (isMultipleSubmission = isSubmitAnotherRecord)
          "
        />
      </template>
      <template v-if="dataCollectionType === 'individual' && renderComponent">
        <DataCollectionIndividual
          v-if="defaultSiteId !== undefined"
          :auditDetail="auditDetail"
          :singleAuditCriteriaId="undefined"
          :editDataCollectionRecord="null"
          :dataCollectionSummary="dirtyDataCollectionSummary"
          :defaultSiteId="defaultSiteId"
          :siteList="siteList"
          :auditCriteriaList="auditCriteriaList"
          :auditConfig="auditConfig"
          @submitRecords="handleEnteredDataRecords"
          @updateSiteDropdownItem="handleSiteDropdownItem"
          @isMultipleSubmission="
            (isSubmitAnotherRecord) =>
              (isMultipleSubmission = isSubmitAnotherRecord)
          "
        />
      </template>
    </div>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import {
  AuditDetail,
  AuditCriteria,
  AuditSite,
  AuditSamplingRequestPayload,
  AuditSamplingResponse,
  DataCollectionSubmitRequestPayload,
  DataCollectionSummaryRequestPayload,
  DataCollectionSummaryResponsePayload,
  AuditSiteListDto,
} from '@/store/modules/audits/types/audits.types';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { ToastProgrammatic as Toast } from 'buefy';
import DataCollectionAggregate from '@/views/Audit/components/DataCollectionAggregate.vue';
import DataCollectionIndividual from '@/views/Audit/components/DataCollectionIndividual.vue';
import { cloneData } from '@/utils/general.util';

@Component({
  components: {
    DataCollectionAggregate,
    DataCollectionIndividual,
  },
})
export default class DataCollectorSheet extends Vue {
  @Prop()
  public auditDetail!: AuditDetail;
  @Prop()
  public auditId!: number;

  public dataCollectionType: string = 'aggregate';
  public dirtyDataCollectionSummary: any = {};
  public renderComponent: boolean = false;
  public defaultSiteId: number | null = null;
  public isMultipleSubmission: boolean = false;

  @Action('audits/getDataCollectionSummary')
  public getDataCollectionSummary!: (
    payload: DataCollectionSummaryRequestPayload,
  ) => void;

  @State((state) => state.audits.dataCollectionSummary)
  public dataCollectionSummary!: DataCollectionSummaryResponsePayload;

  @Action('audits/getAuditCriteria')
  public getAuditCriteria!: (auditId: number) => void;

  @State((state) => state.audits.auditCriteria)
  public auditCriteria!: AuditCriteria[];

  @Action('audits/getAuditSitesForDataCollection')
  public getAuditSitesForDataCollection!: (auditId: number) => void;

  @State((state) => state.audits.auditSites)
  public auditSites!: AuditSite[];

  @Action('audits/getSamplingConfig')
  public getSamplingConfig!: (payload: AuditSamplingRequestPayload) => void;

  @State((state) => state.audits.auditConfig)
  public auditConfig!: AuditSamplingResponse;

  @Action('audits/postDataCollection')
  public fetchDataCollection!: (
    payload: DataCollectionSubmitRequestPayload,
  ) => void;

  @State((state) => state.audits.dataCollectionResponse)
  public dataCollectionResponse!: any;

  @Watch('auditSites')
  @isTruthy
  public watchAuditSites(newValue: AuditSite[]) {
    this.defaultSiteId = newValue[0].id;
  }

  @Watch('auditConfig')
  @isTruthy
  public watchAuditConfig(newValue: AuditSamplingResponse) {
    if (this.dataCollectionSummary) {
      this.renderComponent = true;
    }
  }

  @Watch('dataCollectionSummary')
  @isTruthy
  public watchDataCollectionSummary(
    newValue: DataCollectionSummaryResponsePayload,
  ) {
    this.dirtyDataCollectionSummary = cloneData(newValue);
    if (this.auditConfig) {
      this.renderComponent = true;
    }
  }

  @Watch('dataCollectionResponse')
  @isTruthy
  public watchDataCollectionResponse(newValue: any) {
    Toast.open({
      message: `Data Successfully Submitted.`,
      position: 'is-top',
      type: 'is-dark',
      duration: 3500,
    });
    if (!this.isMultipleSubmission) {
      this.$emit('refreshDataCollectionSheet');
      this.$router.go(-1);
    }
  }

  public mounted() {
    this.getAuditCriteria(this.auditId);
    this.getAuditSitesForDataCollection(this.auditId);
    this.getDataCollectionSummary({
      auditId: this.auditId,
    });
    this.getSamplingConfig({
      auditId: this.auditId,
    });
  }

  public handleSiteDropdownItem(newValue: number) {
    this.defaultSiteId = newValue;
  }

  public handleEnteredDataRecords(
    newValue: DataCollectionSubmitRequestPayload,
  ) {
    if (newValue.aid === '') {
      newValue.aid = null;
    }
    this.fetchDataCollection(newValue);
  }

  public switchMode(newValue: string) {
    this.renderComponent = false;
    this.dataCollectionType = newValue;
    this.$nextTick(() => {
      this.renderComponent = true;
    });
  }

  public compareSites(participantSites: any[]) {
    return (current: any) => {
      return (
        participantSites.filter((participantSite: any) => {
          return participantSite.site.id === current.site.id;
        }).length === 0
      );
    };
  }

  get siteList(): AuditSiteListDto[] {
    if (!this.auditSites || !this.auditSites.length) {
      return [];
    }

    return this.auditSites.map((participantSite) => {
      return {
        siteId: participantSite.site.id,
        name: participantSite.site.name,
        auditSiteId: participantSite.id,
        id: participantSite.id,
      };
    });
  }

  get auditCriteriaList(): AuditCriteria[] {
    return this.auditCriteria ? this.auditCriteria : [];
  }
}
</script>
<style lang="scss" module>
.modeSection {
  margin-top: 28px;
}
.button {
  span {
    display: flex;
    align-items: center;
    font-size: 14px;
    img {
      width: 15px;
      height: 14px;
      margin-right: 5px;
    }
  }
}
.buttonActive {
  color: white;
  background-color: #00205b;
  border: 1px solid #00205b;
  &:hover {
    color: white;
    border: 1px solid #00205b;
  }
}
.dataContainer {
  background-color: #ffffff;
  border-radius: 4px;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.12);
  margin-top: 24px;
  padding: 1.5rem;
  .title {
    font-size: 18px;
    margin-bottom: 1rem;
  }
  .name {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 1rem;
  }
  .type {
    display: flex;
    align-items: center;
    text-transform: capitalize;
    font-size: 16px;
    margin-bottom: 1.5rem;
    .icon {
      height: 16px;
      width: 16px;
      margin-right: 0.5rem;
    }
  }
}
</style>
